import { InfoCircleFilled } from '@ant-design/icons';
import { Avatar, Card, Image, List, Popover, Tag, Typography } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import React, { forwardRef, useEffect, useState } from 'react';
import { defaultStyles, FileIcon } from 'react-file-icon';
import ErrorImg from '../../assets/img/error.png';
import { messageType } from '../../constants';
import { getSendMessageToGroup, handleUpdateFile } from '../../redux/actions/message';
import { getFileUrl } from '../../redux/actions/upload';
import { getUser } from '../../redux/actions/users';
import { extractOriginalFileName, getFullName, safeJsonParse } from '../../utils';
import AudioPlayer from '../AudioPlayer';
import { sender_number } from '../config';
import RetryAttachment from '../RetryAttachment';
import UserAvatar from '../UserAvatar';
import Linkify from 'linkify-react';
import PreviewButtons from '../PreviewButtons';
import { Users2 } from 'lucide-react';
import { encodeId } from '../../utils/secureId';
import { getRoutes } from '../../constants/Routes';
import { NavLink } from 'react-router-dom';

const linkifyOptions = {
    target: '_blank',
    rel: 'noopener noreferrer',
};

const MessageBubble = forwardRef(({ message, user, isHighlighted, isGroup }, ref) => {
    const isSender = message?.from_number === sender_number;
    const [highlight, setHighlight] = useState(isHighlighted);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [sentByName, setSentByName] = useState(null);
    const [sentByGroup, setSentByGroup] = useState(null);
    const [loading, setLoading] = useState(false);

    const fileUrl = getFileUrl('chats', message.contact_id, message.attachment);
    const fileName = extractOriginalFileName(message?.attachment);
    const ext = fileName?.split('.').pop()?.toLowerCase();
    const style = defaultStyles[ext] || {};

    const groupLink = message?.group && getRoutes().groups_detail.route(encodeId(message?.group?.id));

    const isAttachmentMissing = (message) => {
        return !message?.attachment || message.attachment === null;
    };

    const handleRetryAttachment = (message) => {
        if (!message) return;
        const body = {
            contact_id: message?.contact_id,
            message_id: message?.id,
        };
        handleUpdateFile(body);
    };

    const renderMessage = () => {
        switch (message.type) {
            case messageType.TEXT:
                return (
                    <pre className="message-text">
                        <Linkify options={linkifyOptions}>{message?.message}</Linkify>
                    </pre>
                );
            case messageType.IMAGE:
                return isAttachmentMissing(message) ? (
                    <RetryAttachment onRetry={() => handleRetryAttachment(message)} />
                ) : (
                    <div>
                        <Image width={200} src={fileUrl} fallback={ErrorImg} />
                        <pre className="message-text mt-1">
                            <Linkify options={linkifyOptions}>{message?.message}</Linkify>
                        </pre>
                    </div>
                );
            case messageType.VIDEO:
                return isAttachmentMissing(message) ? (
                    <RetryAttachment onRetry={() => handleRetryAttachment(message)} />
                ) : (
                    <div>
                        <video src={fileUrl} width="200" controls />
                        <pre className="message-text mt-1">
                            <Linkify options={linkifyOptions}>{message?.message}</Linkify>
                        </pre>
                    </div>
                );

            case messageType.DOCUMENT:
                return isAttachmentMissing(message) ? (
                    <RetryAttachment onRetry={() => handleRetryAttachment(message)} />
                ) : (
                    <div>
                        <div className="doc-preview-card">
                            <div className="file-icon">
                                <FileIcon extension={ext} {...style} />
                            </div>
                            <div className="container">
                                <div className="title truncate" title={message?.attachment}>
                                    {fileName}
                                </div>
                                <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="view-btn mt-1"
                                >
                                    View Document
                                </a>
                            </div>
                        </div>
                        <pre className="message-text mt-1">
                            <Linkify options={linkifyOptions}>{message?.message}</Linkify>
                        </pre>
                    </div>
                );
            case messageType.AUDIO:
                return isAttachmentMissing(message) ? (
                    <RetryAttachment onRetry={() => handleRetryAttachment(message)} />
                ) : (
                    <div>
                        <AudioPlayer fileUrl={fileUrl} />
                        <pre className="message-text mt-1">
                            <Linkify options={linkifyOptions}>{message?.message}</Linkify>
                        </pre>
                    </div>
                );
            case messageType.STICKER:
                return isAttachmentMissing(message) ? (
                    <RetryAttachment onRetry={() => handleRetryAttachment(message)} />
                ) : (
                    <div>
                        <Image width={200} src={fileUrl} fallback={ErrorImg} preview={false} />
                        <pre className="message-text mt-1">
                            <Linkify options={linkifyOptions}>{message?.message}</Linkify>
                        </pre>
                    </div>
                );
            case messageType.TEMPLATE:
                const templateFileUrl = getFileUrl('template', message.contact_id, message.attachment);
                const params = message?.params ? safeJsonParse(message.params) : null;

                const renderHeader = () => {
                    if (!params?.header) return null;
                    if (params?.header?.[0] !== 'text') {
                        return (
                            <div className="doc-preview-card template">
                                <div className="file-icon">
                                    <FileIcon extension={ext} {...style} />
                                </div>
                                <div className="container">
                                    <div className="title truncate" title={message?.attachment}>
                                        {fileName}
                                    </div>
                                    <a
                                        href={templateFileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="view-btn mt-1"
                                    >
                                        View {ext}
                                    </a>
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <Typography.Title level={5} style={{ margin: 0 }}>
                                {params?.header?.[0]?.text}
                            </Typography.Title>
                        );
                    }
                };

                return (
                    <React.Fragment>
                        <Card
                            title={renderHeader()}
                            className="template-card"
                            actions={
                                params?.footer && [
                                    <Typography.Text type="secondary">{params.footer}</Typography.Text>,
                                ]
                            }
                        >
                            <Typography.Paragraph style={{ marginBottom: 0 }}>
                                {message?.message}
                            </Typography.Paragraph>
                        </Card>
                        {params?.buttons && <PreviewButtons buttons={params?.buttons} />}
                    </React.Fragment>
                );

            default:
                break;
        }
    };

    const handleOpenChange = async (open) => {
        setIsPopoverOpen(open);
        if (open && !sentByName && !loading) {
            setLoading(true);
            try {
                const [userRes, groupRes] = await Promise.all([
                    message?.addedBy && getUser({ id: message?.addedBy, loading: false }),
                    isGroup && message?.group_batch_id
                        ? getSendMessageToGroup(message.group_batch_id)
                        : Promise.resolve(null),
                ]);

                if (userRes?.ack) {
                    const fullName = getFullName(userRes?.user);
                    setSentByName(fullName);
                }

                if (groupRes?.ack) {
                    const members = groupRes?.data?.map((item) => item.name).join(', ');
                    setSentByGroup(members);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
    };

    const messageInfo = (
        <List
            size="small"
            loading={loading}
            dataSource={[
                { label: 'From', value: message?.from_number || 'N/A' },
                {
                    label: 'To',
                    value: isGroup
                        ? loading
                            ? 'Loading...'
                            : sentByGroup || 'Unknown'
                        : message?.to_number || 'N/A',
                },
                {
                    label: 'Sent At',
                    value: dayjs(message?.createdAt).format('DD/MM/YYYY HH:mm:ss'),
                },
                {
                    label: 'Sent By',
                    value: loading ? 'Loading...' : sentByName || 'SYSTEM',
                },
            ]}
            renderItem={(item) => (
                <List.Item>
                    <span style={{ fontWeight: 600, minWidth: 70 }}>{item.label}:</span>
                    <span style={{ marginLeft: 8 }}>{item.value}</span>
                </List.Item>
            )}
        />
    );


    useEffect(() => {
        if (isHighlighted) {
            setHighlight(true);
            const timer = setTimeout(() => setHighlight(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [isHighlighted]);

    return (
        <div
            ref={ref}
            className={clsx('Chat__Message', isSender ? 'sent' : 'received', {
                'highlighted-message': highlight,
                'sending': message?.sending
            })}
        >
            {!isSender && <UserAvatar user={user} isDanger={user?.status === 0} size={32} />}
            <div className="message-bubble">
                {message?.group && (
                    <NavLink to={groupLink}>
                        <Tag
                            icon={
                                <Avatar size={15} icon={<Users2 size={10} />} style={{ backgroundColor: 'gray' }} />
                            }
                            className="mb-1 mr-0 group-tag"
                            color="#0015298b"
                        >
                            {message?.group?.name}
                        </Tag>
                    </NavLink>

                )}
                {renderMessage()}
                <span className="timestamp">
                    {isSender && (
                        <Popover
                            content={messageInfo}
                            trigger="click"
                            open={isPopoverOpen}
                            onOpenChange={handleOpenChange}
                        >
                            <InfoCircleFilled />
                        </Popover>
                    )}
                    {dayjs(message.createdAt).format('HH:mm')}
                </span>
            </div>
        </div>
    );
});

export default MessageBubble;
